import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/useAuth";
import {
  AuthAlert,
  AuthField,
  AuthLayout,
  AuthSubmit,
  AuthSuccess,
  PasswordStrength,
} from "./AuthLayout";

/**
 * First-login password change. The backend flags users created by an
 * admin with `must_change_password=true`; the router sends them here
 * before the main app.
 */
export function ChangePasswordPage() {
  const { user, reload } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const pwError = touched
    ? password.length < 8
      ? "Password must be at least 8 characters."
      : password === current
        ? "New password must be different from the current one."
        : null
    : null;
  const confirmError = touched && confirm !== password ? "Passwords don't match." : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!current || password.length < 8 || password === current || confirm !== password) {
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: password,
      });
      // Server revoked our old session — useAuth will re-login on
      // reload via refresh token, which was also rotated.
      await reload();
      setDone(true);
    } catch {
      setError("Current password is incorrect.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      title="Change password"
      subtitle={
        user?.must_change_password
          ? "Your admin created this account with a temporary password. Choose a new one to continue."
          : "Enter your current password, then choose a new one."
      }
    >
      {done ? (
        <AuthSuccess
          title="Password changed"
          cta={
            <button
              className="btn primary au-go"
              type="button"
              onClick={() => navigate("/", { replace: true })}
            >
              Back to dashboard
            </button>
          }
        >
          <p>Your password has been updated.</p>
        </AuthSuccess>
      ) : (
        <>
          {error && <AuthAlert>{error}</AuthAlert>}
          <form className="au-card" onSubmit={onSubmit} noValidate>
            <AuthField
              id="change-current"
              label="Current password"
              type="password"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              disabled={submitting}
              error={touched && !current ? "Enter your current password." : null}
            />
            <AuthField
              id="change-new"
              label="New password"
              type="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              disabled={submitting}
              error={pwError}
            >
              <PasswordStrength value={password} />
            </AuthField>
            <AuthField
              id="change-confirm"
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              disabled={submitting}
              error={confirmError}
            />
            <AuthSubmit busy={submitting} busyLabel="Saving">
              Change password
            </AuthSubmit>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
